import { AddressData } from "@/common/types/address";
import { ProductCategoryData } from "@/common/types/productCategory.type";
import { optionsObject as companyKinds } from "../../../common/constants/kind.constant";
import { ProductData } from "@/common/types/product.type";
import { DomainData } from "@/common/types/domain";
import { UserData } from "@/common/types/users";
import { BillingCaptureMethod } from "@/common/types/billing/captureMethod.type";
import { cardActivationModeType, PaymentModalityType } from "@/common/types/companies/companies.type";
import { RequirementListSummaryData } from "@/common/types/requirementListSummary.type";
import { ClientUserData } from "@/common/types/client/user.type";
import { CompanyContractConsumptionControlledBy } from "./contract.type";

export enum BranchHierarchyForUserAccess {
  SameOnly = 'same_only',
  SameAndDescendents = 'same_and_descendents',
}

export type EstablishmentProductPrice = {
  id: string;
  product: ProductData;
  updated_at?: string;
  value?: string;
};

export type CompanyBranchProductGrantedCreditData = {
  id?: string;
  product_id: string;
  granted_credit: string;
  granted_quantity?: string;
  product: ProductData;
};

export type CompanyBranchProductCategoryGrantedCreditData = {
  id?: string;
  product_category_id: string;
  granted_credit: string;
  granted_quantity?: string;
  product_category: ProductCategoryData;
  product_granted_credits?: CompanyBranchProductGrantedCreditData[];
};

export type CompanyBranchData = {
  id: string,
  client_users_count: number,
  created_at: string,
  display_name: string,
  equipments_count: number,
  parent_id: string,
  parent_display_name: string,
  master_client_user?: {
    id: string;
    full_name: string;
  };
  general_information?: GeneralInformationData;
  restriction: RestrictionData;
  consumed_credit: string;
  remaining_credit: string;
  granted_credit: string;
  product_category_granted_credits?: CompanyBranchProductCategoryGrantedCreditData[];
  government_allocation_requested_credit: string;
  total_government_allocation_distributed_to_branch: string;
  user_branch_accesses: {
    id: string;
    user: ClientUserData;
    branch_hierarchy_for_user_access: BranchHierarchyForUserAccess;
  }[]
};

export type EstablishmentCompanyBranchData = {
  capture_methods?: {
    id: string;
    capture_method: BillingCaptureMethod;
  }[];
  general_information: GeneralInformationData;
  display_name?: string;
  photos: {
    id: string;
    attachment_id: string;
    url: string;
    content_type: string;
  }[];
};

export enum ContactState {
  pending = 'pending',
  awaiting_client_documents = 'awaiting_client_documents',
  in_review = 'in_review',
  complete = 'complete'
}

export type ContactData = {
  id: string;
  email?: string;
  full_name?: string;
  last_review?: LastReview;
  role_in_company?: string;
  phone?: {
    number: string;
    kind: string;
    whatsapp_capability: string;
  };
  responsible_for_company_information?: boolean;
  documents?: {
    id?: string;
    cpf?: string;
    file?: {
      url: string;
      content_type: string;
    };
    issued_on_date?: string;
    expires_at?: string;
    rg?: string;
    type?: string;
    last_review?: LastReview;
    uploaded_by?: UserData;
    cnh?: string;
  }[];
  requirement_list_summary?: RequirementListSummaryData;
  state: ContactState;
};

export type EstablishmentCompanyData = {
  id: string;
  domains: DomainData[];
  general_information: GeneralInformationData | null;
  commercial_manager: UserData;
  lifetime_revenue_amount: string;
  unpaid_fees_total: string;
  observations: string;
  receivable_amount: string;
  users_count: number;
  matrix_branch_id: string;
  matrix_branch?: EstablishmentCompanyBranchData;
  contacts: ContactData[];
  current_contract_id: string;
  fee_total: string;
  product_prices?: EstablishmentProductPrice[];
};

export type EstablishmentCompanyResponse = {
  data: {
    data: EstablishmentCompanyData;
  };
};


export type AllowedOrBlockedEstablishments = {
  id: string;
  establishment_branch: {
    id: string;
    general_information: EstablishmentCompanyData['general_information'];
  };
};

export type RestrictionData = {
  credit_limit: string;
  minimum_kilometers_usage_between_consumptions: number;
  maximum_kilometers_usage_between_consumptions: number;
  minimum_seconds_interval_between_consumptions: number;
  minimum_seconds_usage_between_consumptions: number;
  maximum_seconds_usage_between_consumptions: number;
  period_restrictions: {
    id: string;
    blocked: boolean;
    intervals: {
      id: string;
      end_time: string;
      start_time: string;
    }[];
    week_day: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
  }[];
  product_category_restrictions: {
    id: string;
    product_category: {
      id: string;
      display_name: string;
      products_count: number;
    };
    minimum_kilometers_usage_between_consumptions: number;
    maximum_kilometers_usage_between_consumptions: number;
    minimum_seconds_usage_between_consumptions: number;
    maximum_seconds_usage_between_consumptions: number;
  }[];
  product_restrictions: {
    id: string;
    credit_limit: string;
    product: {
      id: string;
      category: {
        id: string;
        display_name: string;
        products_count: number;
      };
      display_name: string;
      unit_for_quantity: string;
    };
    quantity_limit: string;
  }[];
};

export type LastReview = {
  description?: string;
  state: 'disapproved' | 'approved';
};

export enum CompanyRequireBillReviewEnum {
  Always = 'always',
  None = 'none',
}

export type GeneralInformationData = {
  id?: string;
  address: AddressData;
  cnpj?: string;
  email?: string;
  fantasy_name?: string;
  official_name?: string;
  primary_cnae?: string;
  score?: string;
  state_inscription?: string;
  company_logo?: {
    url: string;
    content_type: string;
  };
  website?: string;
  last_review?: LastReview;
};

export type MatrixBranchData = {
  id: string;
  display_name: string;
  equipments_count: number;
  general_information: GeneralInformationData;
  product_category_granted_credits?: CompanyBranchProductCategoryGrantedCreditData[];
  restriction: RestrictionData;
  master_client_user?: {
    id: string;
    full_name: string;
  };
};

export type CompanyData = {
  id: string;
  current_contract_id: string;
  general_information: GeneralInformationData;
  current_contract: {
    id: string;
    granted_credit: string;
    product_categories: ProductCategoryData[];
    payment_modality: PaymentModalityType;
    consumption_controlled_by: CompanyContractConsumptionControlledBy;
  };
  card_activation_mode: cardActivationModeType
  kind: keyof typeof companyKinds;
  granted_credit: string;
  unallocated_credit: string;
  matrix_branch: MatrixBranchData;
  price_survey_mode: 'disabled' | 'informational_only' | 'used_as_price_limit'
  require_bill_review: CompanyRequireBillReviewEnum;
  update_equipment_log_reason_requirement: 'none' | 'required';
  update_user_log_reason_requirement: 'none' | 'required';
  branch_hierarchy_for_user_access: 'same_and_descendents' | 'all' | 'dynamic'
  government_allocation_currency_restricted?: boolean;
  request_personal_card_mode: 'allowed' | 'blocked';
};

export type CompanyBranchResponse = {
  data: {
    data: CompanyBranchData;
  };
};

export type CompanyResponse = {
  data: {
    data: CompanyData;
  };
};
